import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography, CircularProgress, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { toast } from 'react-toastify';

import { GT_COLORS } from '../../../components/groups-teams/shared/constants';
import { NotificationRulesService } from '../../../services/notification-rules.service';
import {
    ALERT_TYPE_LABEL,
    CHECK_ON_ME_WHEN,
    NotificationAlertType,
    RoutingOverview,
    RoutingRule,
} from '../../../types/notification-rules.types';
import { alertStatus, AlertStatus } from '../../../components/notification-rules/routing-copy';
import { DispatchContactsEditor } from '../../../components/notification-rules/DispatchContactsEditor';
import { PeopleField } from '../../../components/notification-rules/PeopleField';
import { DescribedOption, DescribedSelect } from '../../../components/notification-rules/DescribedSelect';
import { createSystemUserPermissionCheck } from '../../../helpers/permissionCheck';
import { customerPermissionFlags } from '../../../constants/systemPermissions.constants';
import { PointOfContactSelect } from '../../../components/notification-rules/PointOfContactSelect';
import { NotificationRow } from '../../../components/notification-rules/NotificationRow';
import { ALERT_TYPE_EXPLAINERS } from '../../../components/notification-rules/alert-type-explainers';

/**
 * Settings → Notifications.
 *
 * Built so someone who knows only what each alert is and who their people are
 * can set it up: one choice per alert type, one point of contact, and an
 * optional list for when EMS is sent. Every status line comes from the routing
 * the API actually resolves, so the page cannot describe something that will
 * not happen.
 */

const STAGE_LABEL: Record<string, string> = {
    triggered: 'As it happens',
    resolved: 'After it’s resolved',
};

// One sentence per option, shown only while the dropdown is open: this is
// where the page explains itself, at the moment someone is choosing.
const STAGE_HELP: Record<string, string> = {
    triggered: 'Your people hear about it while Dispatch is still handling it.',
    resolved: 'A summary once it’s closed, so nobody is interrupted while it’s happening.',
};

// The recommended option is listed first.
const STAGE_ORDER = ['resolved', 'triggered'];
const orderStages = (stages: string[]) => [...stages].sort((a, b) => {
    const ia = STAGE_ORDER.indexOf(a);
    const ib = STAGE_ORDER.indexOf(b);
    return (ia === -1 ? Infinity : ia) - (ib === -1 ? Infinity : ib);
});

const WHO_OPTIONS: DescribedOption[] = [
    {
        value: 'leaders',
        label: 'Team leaders',
        description: 'The leaders of the POM User’s team when the alert happens. '
            + 'Anyone who uses Get alerts on that team is included.',
    },
    {
        value: 'named',
        label: 'Specific people',
        description: 'The same people every time, whatever team the alert comes from.',
    },
];

type RulePatch = { teamRouting?: boolean; recipientIds?: string[]; stages?: string[] };

const Card = ({ children }: { children: React.ReactNode }) => (
    <Box sx={{
        backgroundColor: GT_COLORS.cardBg,
        border: `1px solid ${GT_COLORS.border}`,
        borderRadius: '10px',
        padding: { xs: '4px 16px', sm: '4px 24px' },
        maxWidth: 880,
    }}>
        {children}
    </Box>
);

const StatusLine = ({ status }: { status: AlertStatus }) => {
    const bad = status.tone === 'bad';
    return (
        <Typography sx={{
            fontSize: '13px',
            lineHeight: 1.5,
            padding: '8px 12px',
            borderRadius: '8px',
            border: `1px solid ${bad ? GT_COLORS.redBorder : GT_COLORS.amberBorder}`,
            backgroundColor: bad ? GT_COLORS.redBg : GT_COLORS.amberBg,
            color: bad ? GT_COLORS.red : GT_COLORS.amberDark,
        }}>
            {status.text}
        </Typography>
    );
};

export const NotificationsPage = () => {
    // Same rule as the Settings page: only Super Admins (and POM Master) can
    // change these. Everyone else sees the organization's setup, read-only.
    const canEdit = createSystemUserPermissionCheck(customerPermissionFlags.SUPER_ADMIN);
    const [overview, setOverview] = useState<RoutingOverview | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    // Choosing "Specific people" does not save on its own. A list with nobody
    // in it would leave the alert type pointing at no one, so the switch is
    // committed together with the first person added.
    const [pending, setPending] = useState<Partial<Record<NotificationAlertType, boolean>>>({});

    const load = useCallback(async () => {
        try {
            setOverview(await NotificationRulesService.fetchOverview());
        } catch {
            toast.error('Could not load notification settings.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const patchRule = useCallback(async (alertType: NotificationAlertType, patch: RulePatch) => {
        setSaving(true);
        try {
            await NotificationRulesService.updateRule(alertType, patch);
            await load();
            toast.success('Saved.');
        } catch {
            toast.error('Could not save. Nothing was changed.');
        } finally {
            setSaving(false);
        }
    }, [load]);

    const setPointOfContact = useCallback(async (userId: string | null) => {
        setSaving(true);
        try {
            setOverview(await NotificationRulesService.setPointOfContact(userId));
            toast.success('Saved.');
        } catch {
            toast.error('Could not save. Nothing was changed.');
        } finally {
            setSaving(false);
        }
    }, []);

    if (loading) {
        return <Box sx={{ padding: '40px', textAlign: 'center' }}><CircularProgress size={28} /></Box>;
    }
    if (!overview) {
        return <Typography sx={{ padding: '24px' }}>Notification settings are unavailable right now.</Typography>;
    }

    // A reason that comes from team structure, such as "no team has a leader
    // yet", is the same for every alert type set to team leaders. Say it once
    // at the top rather than repeating it on each of those rows.
    const leaderRules = overview.rules.filter((r) => r.teamRouting && !pending[r.alertType]);
    const sharedStatus = leaderRules.length > 1
        ? alertStatus(leaderRules[0], overview, 'team leader alerts')
        : null;

    const renderAlertType = (rule: RoutingRule) => {
        const isPending = Boolean(pending[rule.alertType]);
        const mode = isPending || !rule.teamRouting ? 'named' : 'leaders';
        const status = isPending || (rule.teamRouting && sharedStatus) ? null : alertStatus(rule, overview);

        return (
            <NotificationRow
                key={rule.alertType}
                label={ALERT_TYPE_LABEL[rule.alertType]}
                explainer={ALERT_TYPE_EXPLAINERS[rule.alertType]}
            >
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px 16px' }}>
                    <DescribedSelect
                        value={mode}
                        options={WHO_OPTIONS}
                        ariaLabel={`Who gets ${ALERT_TYPE_LABEL[rule.alertType]} alerts`}
                        disabled={saving || !canEdit}
                        onChange={(next) => {
                            if (next === 'named') {
                                if (rule.teamRouting) setPending((p) => ({ ...p, [rule.alertType]: true }));
                                return;
                            }
                            setPending((p) => ({ ...p, [rule.alertType]: false }));
                            if (!rule.teamRouting) patchRule(rule.alertType, { teamRouting: true });
                        }}
                    />

                    {rule.alertType === 'CheckOnMe' && (
                        // Check On Me's timing is an existing organization setting
                        // with its own dependencies (it is off whenever Check On Me
                        // or its escalation is off), so it is shown here and
                        // changed where it lives.
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <Typography sx={{ fontSize: '13px', color: GT_COLORS.textMuted }}>Notify</Typography>
                            <Typography sx={{ fontSize: '14px' }}>
                                {CHECK_ON_ME_WHEN[rule.stageValue ?? 'EXPIRED_ESCALATED'] ?? rule.stageValue}
                            </Typography>
                            {canEdit && (
                                <Link component={RouterLink} to="/settings" underline="hover" sx={{ fontSize: '13px' }}>
                                    Change in Settings
                                </Link>
                            )}
                        </Box>
                    )}

                    {rule.alertType === 'SOS' && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Typography sx={{ fontSize: '13px', color: GT_COLORS.textMuted }}>Notify</Typography>
                            <DescribedSelect
                                value={rule.stages[0] ?? 'triggered'}
                                options={orderStages(rule.stageOptions).map((stage) => ({
                                    value: stage,
                                    label: STAGE_LABEL[stage] ?? stage,
                                    description: STAGE_HELP[stage] ?? '',
                                    badge: stage === 'resolved' ? 'Recommended' : undefined,
                                }))}
                                ariaLabel="When to notify about an SOS"
                                disabled={saving || !canEdit}
                                minWidth={190}
                                onChange={(stage) => patchRule(rule.alertType, { stages: [stage] })}
                            />
                        </Box>
                    )}
                </Box>

                {mode === 'named' && (
                    <PeopleField
                        options={overview.systemUsers}
                        value={rule.named}
                        ariaLabel={`People notified for ${ALERT_TYPE_LABEL[rule.alertType]}`}
                        disabled={saving || !canEdit}
                        onChange={(next) => {
                            setPending((p) => ({ ...p, [rule.alertType]: false }));
                            if (next.length === 0) {
                                // A list with nobody in it is not allowed, so removing the
                                // last person hands these alerts back to team leaders.
                                if (!rule.teamRouting) {
                                    toast.info('That was the last person, so these alerts go back to team leaders.');
                                    patchRule(rule.alertType, { teamRouting: true });
                                }
                                return;
                            }
                            patchRule(rule.alertType, {
                                teamRouting: false,
                                recipientIds: next.map((p) => p.userId),
                            });
                        }}
                    />
                )}

                {isPending && (
                    <Typography sx={{ fontSize: '13px', color: GT_COLORS.textMuted }}>
                        Add at least one person. Until then, these alerts stay with team leaders.
                    </Typography>
                )}

                {status && <StatusLine status={status} />}
            </NotificationRow>
        );
    };

    return (
        <Box sx={{ padding: { xs: '16px', sm: '24px 28px 48px' }, display: 'grid', gap: '12px' }}>
            <Box sx={{ marginBottom: '8px' }}>
                <Typography sx={{ fontSize: '20px', fontWeight: 600 }}>Notifications</Typography>
                <Typography sx={{ fontSize: '14px', color: GT_COLORS.textMuted, maxWidth: '62ch' }}>
                    Choose who POM tells when something happens to one of your POM Users. People are reached
                    by the email, text or call settings on their own profile.
                </Typography>
            </Box>

            {!canEdit && (
                <Typography sx={{
                    fontSize: '13px',
                    color: GT_COLORS.textSecondary,
                    backgroundColor: GT_COLORS.primaryLight,
                    border: `1px solid ${GT_COLORS.border}`,
                    borderRadius: '8px',
                    padding: '10px 12px',
                    maxWidth: 880,
                }}>
                    You can see how your organization is set up, but only a Super Admin can change it.
                </Typography>
            )}

            <Typography sx={{ fontSize: '16px', fontWeight: 600 }}>Who gets notified?</Typography>
            <Card>
                {sharedStatus && (
                    <Box sx={{ paddingTop: '18px' }}>
                        <StatusLine status={sharedStatus} />
                    </Box>
                )}
                {overview.rules.map(renderAlertType)}
                <NotificationRow label="Point of contact" hint="Gets alerts that have no one else to go to, such as from a team with no leader.">
                    <PointOfContactSelect
                        overview={overview}
                        disabled={saving || !canEdit}
                        onChange={setPointOfContact}
                    />
                </NotificationRow>
            </Card>

            <Box sx={{ marginTop: '20px' }}>
                <Typography sx={{ fontSize: '16px', fontWeight: 600 }}>
                    When EMS is sent to an SOS, also call or email
                </Typography>
                <Typography sx={{ fontSize: '13px', color: GT_COLORS.textMuted, maxWidth: '62ch' }}>
                    For a security desk or hotline. They don’t need a POM login.
                </Typography>
            </Box>
            <Card>
                <Box sx={{ padding: '18px 0' }}>
                    <DispatchContactsEditor overview={overview} onSaved={setOverview} readOnly={!canEdit} />
                </Box>
            </Card>
        </Box>
    );
};
